import { EventEmitter, Injectable } from "@angular/core";
import { Socket } from "ngx-socket-io";
import { SessionService } from "../session.service";
import { environment } from "../../environments/environment";
import { Notification } from "./notification";
import { UserNotificationsHandlerService } from "../handlers/user-notifications-handler.service";
import { AccountAppDTO } from "../apps/AccountApp";
import { UserNotificationDTO } from "./UserNotificationDTO";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ErrorSnackBarComponent } from "./error-snack-bar.component";
import { SuccessSnackBarComponent } from "./success-snack-bar.component";
import { UiService } from "../ui/ui.service";

const NOTIFICATIONS_PER_PAGE = 50;

@Injectable()
export class NotificationsSocket extends Socket {
  constructor() {
    super({
      url: `${environment.serviceURL}/notifications`,
      options: { autoConnect: false },
    });
  }
}

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  notifications: Notification[] = [];
  gotAllNotifications = false;
  toggleNotificationsEvent = new EventEmitter<void>();
  unreadNotificationCount = 0;
  constructor(
    private socket: NotificationsSocket,
    private SessionService: SessionService,
    private UserNotificationsHandler: UserNotificationsHandlerService,
    private SnackBar: MatSnackBar,
    private uiService: UiService,
  ) {
    this.SessionService.currentApp.subscribe((app: AccountAppDTO | null) => {
      if (app) {
        this.initNotifications(app.id);
      } else {
        this.notifications = [];
      }
    });
  }

  get unreadNotifications() {
    return this.notifications.filter((notification) => !notification.viewed);
  }

  openSuccessSnackBar(message = "Success") {
    this.SnackBar.openFromComponent(SuccessSnackBarComponent, {
      duration: 6000,
      data: message,
      horizontalPosition: "right",
      panelClass: ["result-snackbar", "success-snackbar"],
    });
  }

  openErrorSnackBar(message = "Error") {
    this.SnackBar.openFromComponent(ErrorSnackBarComponent, {
      duration: 6000,
      data: message,
      horizontalPosition: "right",
      panelClass: ["result-snackbar", "error-snackbar"],
    });
  }

  deleteNotification(notification: Notification) {
    this.uiService
      .waitForEvent(
        this.UserNotificationsHandler.userNotificationsDelete(
          notification.userNotificationDTO,
        ),
      )
      .subscribe(() => {
        this.initNotifications(
          this.SessionService.currentAppValue!.id,
          this.notifications.length - 1,
        );
      });
  }

  markAsRead(notification: Notification) {
    notification.viewed = true;
    this.uiService
      .waitForEvent(
        this.UserNotificationsHandler.userNotificationsPut(
          notification.userNotificationDTO,
        ),
      )
      .subscribe(() => {
        this.initNotifications(
          this.SessionService.currentAppValue!.id,
          this.notifications.length,
        );
      });
  }

  markAllAsRead() {
    this.UserNotificationsHandler.markAllAsRead(
      this.SessionService.currentAppValue!.id,
    ).subscribe(() => {
      this.initNotifications(
        this.SessionService.currentAppValue!.id,
        this.notifications.length - 1,
      );
    });
  }

  deleteAll() {
    this.uiService
      .waitForEvent(
        this.UserNotificationsHandler.deleteAll(
          this.SessionService.currentAppValue!.id,
        ),
      )
      .subscribe(() => {
        this.initNotifications(this.SessionService.currentAppValue!.id);
      });
  }

  private initNotifications(appID: number, count?: number) {
    this.uiService
      .waitForEvent(
        this.UserNotificationsHandler.userNotificationsGet(
          appID,
          count ?? NOTIFICATIONS_PER_PAGE,
        ),
      )
      .subscribe((result) => {
        this.notifications = (result as unknown as UserNotificationDTO[]).map(
          (userNotificationDTO) => new Notification(userNotificationDTO),
        );
        this.gotAllNotifications =
          this.notifications.length < NOTIFICATIONS_PER_PAGE;
      });
    this.uiService
      .waitForEvent(this.UserNotificationsHandler.unreadCount(appID))
      .subscribe(
        (result) =>
          (this.unreadNotificationCount = result as unknown as number),
      );
  }

  getMoreNotifications() {
    this.uiService
      .waitForEvent(
        this.UserNotificationsHandler.userNotificationsGet(
          this.SessionService.currentAppValue!.id,
          NOTIFICATIONS_PER_PAGE,
          Math.floor(this.notifications.length / NOTIFICATIONS_PER_PAGE),
        ),
      )
      .subscribe((result) => {
        for (let notification of result as unknown as UserNotificationDTO[]) {
          this.notifications.push(new Notification(notification));
        }
        this.gotAllNotifications =
          (result as unknown as UserNotificationDTO[]).length <
          NOTIFICATIONS_PER_PAGE;
      });
  }
}
